import { useCallback, useEffect, useRef, useState } from 'react';
import { rescheduleServiceRequest } from '../api/rescheduleServiceRequestApiService';
import { RESCHEDULE_SR_LABELS as L } from '../constants/labels';
import { RESCHEDULE_SR_ERROR_MESSAGES } from '../errors/errorMessages';
import type { ServiceRequestStatus } from '../types/serviceRequestListType';
import type {
  RescheduleServiceRequestResponseData,
  RescheduleErrorKind,
} from '../types/rescheduleServiceRequestType';
import { parseWarsawIso } from '../pages/viewServiceRequest/viewServiceRequestHelpers';
import { isReschedulableNow } from '../utils/reschedulePolicy';
import {
  toIsoDate,
  validateDateOrder,
  validateTimeOrder,
  validateMinDuration,
} from '../utils/scheduleWindowValidation';

export type RescheduleStep = 'form' | 'confirm';
export type RescheduleAssignToRole = 'technician' | 'admin' | '';

/** Snapshot frozen at the moment the modal opens — feeds the confirm screen. */
export interface ReschedulePreviousSnapshot {
  technicianName: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
}

interface ReschedulePrefill {
  scheduledStartAt: string | null | undefined;
  scheduledEndAt: string | null | undefined;
  technicianName: string | null | undefined;
}

interface UseRescheduleOptions {
  serviceRequestId: string | undefined;
  currentStatus: ServiceRequestStatus | undefined;
  prefill: ReschedulePrefill;
  onSuccess?: (data: RescheduleServiceRequestResponseData | undefined) => void;
  /** Fired on 409 status_changed — page should refetch + close. */
  onStaleStatus?: () => void;
}

export interface UseRescheduleReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  // Step
  step: RescheduleStep;
  goToConfirm: () => void;
  goBackToForm: () => void;

  // Snapshot of original values (frozen at open-time).
  previousSnapshot: ReschedulePreviousSnapshot | null;

  // Window
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  setStartDate: (d: Date | undefined) => void;
  setEndDate: (d: Date | undefined) => void;
  setStartTime: (t: string) => void;
  setEndTime: (t: string) => void;
  startDateError: string;
  endDateError: string;
  endTimeError: string;
  windowComplete: boolean;

  // Assignee
  assignToRole: RescheduleAssignToRole;
  setAssignToRole: (role: RescheduleAssignToRole) => void;
  assignToError: string;

  // Technician
  selectedTechnicianId: string;
  selectedTechnicianCode: string;
  selectedTechnicianName: string;
  setSelectedTechnician: (
    id: string,
    technicianCode: string,
    name?: string
  ) => void;
  technicianError: string;
  prefillTechnicianName: string;
  /** Toggles each time the modal should imperatively re-open the dropdown
   *  (e.g. after a 409 technician-conflict bounce). */
  forceReopenTechnicianAt: number;

  // Notify toggle (confirm step)
  notify: boolean;
  setNotify: (value: boolean) => void;

  // Submission
  isSubmitting: boolean;
  submitError: string;
  canReview: boolean;
  canSubmit: boolean;
  submit: () => Promise<void>;

  // Derived ISO strings for the eligibility query.
  startDateIso: string;
  endDateIso: string;
}

/** Convert a UTC ISO timestamp into a Warsaw-wall-clock `Date` (date-only)
 *  + `HH:mm` string. */
const splitIso = (
  iso: string | null | undefined
): { date: Date | undefined; time: string } => {
  const parsed = parseWarsawIso(iso);
  if (!parsed) {
    return { date: undefined, time: '' };
  }
  return parsed;
};

export const useReschedule = ({
  serviceRequestId,
  currentStatus,
  prefill,
  onSuccess,
  onStaleStatus,
}: UseRescheduleOptions): UseRescheduleReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<RescheduleStep>('form');

  const [startDate, setStartDateState] = useState<Date | undefined>(undefined);
  const [endDate, setEndDateState] = useState<Date | undefined>(undefined);
  const [startTime, setStartTimeState] = useState<string>('');
  const [endTime, setEndTimeState] = useState<string>('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  const [assignToRole, setAssignToRoleState] =
    useState<RescheduleAssignToRole>('');
  const [assignToError, setAssignToError] = useState('');
  const [selectedTechnicianId, setSelectedTechnicianIdState] = useState('');
  const [selectedTechnicianCode, setSelectedTechnicianCodeState] = useState('');
  const [selectedTechnicianName, setSelectedTechnicianNameState] = useState('');
  const [technicianError, setTechnicianError] = useState('');
  const [forceReopenTechnicianAt, setForceReopenTechnicianAt] = useState(0);

  const [notify, setNotify] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const previousSnapshotRef = useRef<ReschedulePreviousSnapshot | null>(null);
  const [previousSnapshot, setPreviousSnapshot] =
    useState<ReschedulePreviousSnapshot | null>(null);
  const [prefillTechnicianName, setPrefillTechnicianName] = useState('');

  const resetForm = useCallback(() => {
    setStartDateState(undefined);
    setEndDateState(undefined);
    setStartTimeState('');
    setEndTimeState('');
    setStartDateError('');
    setEndDateError('');
    setEndTimeError('');
    setAssignToRoleState('');
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setSelectedTechnicianNameState('');
    setTechnicianError('');
    setSubmitError('');
    setStep('form');
    setPreviousSnapshot(null);
    previousSnapshotRef.current = null;
    setPrefillTechnicianName('');
    setNotify(false);
  }, []);

  const open = useCallback(() => {
    // The new-time fields stay empty — the admin must enter a fresh window.
    // We still capture the existing slot in `previousSnapshot` for the
    // side-by-side comparison on the confirm step.
    const start = splitIso(prefill.scheduledStartAt);
    const end = splitIso(prefill.scheduledEndAt);
    const seededTechName = prefill.technicianName?.trim() ?? '';

    setStartDateState(undefined);
    setEndDateState(undefined);
    setStartTimeState('');
    setEndTimeState('');
    setStartDateError('');
    setEndDateError('');
    setEndTimeError('');
    setAssignToRoleState('');
    setAssignToError('');
    // The SR detail carries the technician's *user* id but the BE wants
    // `technicians.id`. We require a fresh pick from the dropdown.
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setSelectedTechnicianNameState('');
    setTechnicianError('');
    setSubmitError('');
    setStep('form');
    setPrefillTechnicianName(seededTechName);
    setNotify(false);

    const snapshot: ReschedulePreviousSnapshot = {
      technicianName: seededTechName,
      startDate: start.date,
      endDate: end.date,
      startTime: start.time,
      endTime: end.time,
    };
    previousSnapshotRef.current = snapshot;
    setPreviousSnapshot(snapshot);
    setIsOpen(true);
  }, [
    prefill.scheduledStartAt,
    prefill.scheduledEndAt,
    prefill.technicianName,
  ]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  const windowComplete = Boolean(startDate && endDate);

  const computeValidation = useCallback((): {
    dateErr: string;
    timeErr: string;
  } => {
    const dateErr = validateDateOrder(startDate, endDate, {
      startAfterEnd: L.ERROR_START_DATE_AFTER_END,
    });
    const timeErr =
      validateTimeOrder(startDate, endDate, startTime, endTime, {
        startTimeAfterEnd: L.ERROR_START_TIME_AFTER_END,
      }) ||
      validateMinDuration(startDate, endDate, startTime, endTime, {
        minDuration: L.ERROR_MIN_DURATION,
      });
    return { dateErr, timeErr };
  }, [startDate, endDate, startTime, endTime]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const { dateErr, timeErr } = computeValidation();
    if (startDate && endDate) {
      setEndDateError(dateErr);
    }
    setEndTimeError(timeErr);
  }, [isOpen, startDate, endDate, computeValidation]);

  const clearPostWindowState = useCallback(() => {
    setAssignToError('');
    setSelectedTechnicianIdState('');
    setSelectedTechnicianCodeState('');
    setSelectedTechnicianNameState('');
    setTechnicianError('');
    setSubmitError('');
  }, []);

  const setStartDate = useCallback(
    (d: Date | undefined) => {
      setStartDateState(d);
      setStartDateError('');
      setEndDateError('');
      clearPostWindowState();
    },
    [clearPostWindowState]
  );

  const setEndDate = useCallback(
    (d: Date | undefined) => {
      setEndDateState(d);
      setEndDateError('');
      clearPostWindowState();
    },
    [clearPostWindowState]
  );

  const setStartTime = useCallback(
    (t: string) => {
      setStartTimeState(t);
      setEndTimeError('');
      clearPostWindowState();
    },
    [clearPostWindowState]
  );

  const setEndTime = useCallback(
    (t: string) => {
      setEndTimeState(t);
      setEndTimeError('');
      clearPostWindowState();
    },
    [clearPostWindowState]
  );

  const setAssignToRole = useCallback((role: RescheduleAssignToRole) => {
    setAssignToRoleState(role);
    setAssignToError('');
    if (role !== 'technician') {
      setSelectedTechnicianIdState('');
      setSelectedTechnicianCodeState('');
      setSelectedTechnicianNameState('');
      setTechnicianError('');
    }
    setSubmitError('');
  }, []);

  const setSelectedTechnician = useCallback(
    (id: string, technicianCode: string, name?: string) => {
      setSelectedTechnicianIdState(id);
      setSelectedTechnicianCodeState(technicianCode);
      if (name !== undefined) {
        setSelectedTechnicianNameState(name);
      }
      setTechnicianError('');
      setSubmitError('');
    },
    []
  );

  const goToConfirm = useCallback(() => {
    // Run full validation before advancing — same as submit-time guard.
    let hasError = false;
    if (!startDate) {
      setStartDateError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (!endDate) {
      setEndDateError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (startDate && endDate) {
      const { dateErr, timeErr } = computeValidation();
      if (dateErr) {
        setEndDateError(dateErr);
        hasError = true;
      }
      if (timeErr) {
        setEndTimeError(timeErr);
        hasError = true;
      }
    }
    if (startDate && endDate && !assignToRole) {
      setAssignToError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (assignToRole === 'technician' && !selectedTechnicianId) {
      setTechnicianError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (hasError) {
      return;
    }
    setStep('confirm');
  }, [
    startDate,
    endDate,
    assignToRole,
    selectedTechnicianId,
    computeValidation,
  ]);

  const goBackToForm = useCallback(() => {
    setStep('form');
  }, []);

  const submit = useCallback(async () => {
    if (!serviceRequestId) {
      return;
    }
    // Defensive 24h re-check — should be unreachable if the page CTA gate
    // works, but guards against the SR row changing between page-load and
    // submit.
    const eligibility = isReschedulableNow(
      currentStatus,
      prefill.scheduledStartAt
    );
    if (!eligibility.canReschedule) {
      const message =
        eligibility.reason === 'WITHIN_24H'
          ? L.ERROR_24H_RULE
          : L.ERROR_NOT_RESCHEDULABLE;
      setSubmitError(message);
      setStep('form');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const result = await rescheduleServiceRequest({
        serviceRequestId,
        technicianId: selectedTechnicianId,
        startDate: toIsoDate(startDate),
        endDate: toIsoDate(endDate),
        startTime,
        endTime,
        notify,
      });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.(result.data);
        return;
      }

      const kind: RescheduleErrorKind = result.errorKind ?? 'GENERIC';
      if (kind === 'STATUS_CHANGED') {
        // Page handles refetch + close.
        setIsOpen(false);
        resetForm();
        onStaleStatus?.();
        return;
      }
      if (kind === 'TECHNICIAN_CONFLICT') {
        setStep('form');
        setSelectedTechnicianIdState('');
        setSelectedTechnicianCodeState('');
        setSelectedTechnicianNameState('');
        setTechnicianError(
          result.error ?? RESCHEDULE_SR_ERROR_MESSAGES.TECHNICIAN_CONFLICT
        );
        setForceReopenTechnicianAt(Date.now());
        return;
      }
      // WITHIN_24H, NOT_RESCHEDULABLE, GENERIC — show inline on the form.
      setStep('form');
      setSubmitError(
        result.error ?? RESCHEDULE_SR_ERROR_MESSAGES.DEFAULT_ERROR
      );
    } catch {
      setSubmitError(RESCHEDULE_SR_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setStep('form');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    serviceRequestId,
    currentStatus,
    prefill.scheduledStartAt,
    selectedTechnicianId,
    startDate,
    endDate,
    startTime,
    endTime,
    notify,
    onSuccess,
    onStaleStatus,
    resetForm,
  ]);

  const canReview =
    !!startDate &&
    !!endDate &&
    !!assignToRole &&
    (assignToRole !== 'technician' || !!selectedTechnicianId) &&
    !endDateError &&
    !endTimeError;
  const canSubmit = !isSubmitting && !!serviceRequestId && canReview;

  return {
    isOpen,
    open,
    close,
    step,
    goToConfirm,
    goBackToForm,
    previousSnapshot,
    startDate,
    endDate,
    startTime,
    endTime,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    startDateError,
    endDateError,
    endTimeError,
    windowComplete,
    assignToRole,
    setAssignToRole,
    assignToError,
    selectedTechnicianId,
    selectedTechnicianCode,
    selectedTechnicianName,
    setSelectedTechnician,
    technicianError,
    prefillTechnicianName,
    forceReopenTechnicianAt,
    notify,
    setNotify,
    isSubmitting,
    submitError,
    canReview,
    canSubmit,
    submit,
    startDateIso: toIsoDate(startDate),
    endDateIso: toIsoDate(endDate),
  };
};
